import React, { ChangeEvent, FC } from "react";
import AppSearchBox from "./searchBox";
import DualListboxCheckboxItem from "./dualListboxCheckboxItem";
import { AdminFormFieldSubmit } from "./admin/formFields";
import { useTranslation } from "react-i18next";
import { TCustomizableItemList } from "./types";
import svgIcons from "../assets/svg/svgIcons";
import { DragDropContext, Droppable } from "@hello-pangea/dnd";

type ItemsProps = {
  title: string;
  searchPlaceholder: string;
  searchCallback: (e: React.ChangeEvent<HTMLInputElement>) => void;
  listOfItems: TCustomizableItemList[];
  handleCheckboxChange: ({
    item,
    isChecked,
  }: {
    item: any;
    isChecked: boolean;
  }) => void;
  disabled?: boolean;
  groupPage: boolean;
  showInactiveVehicles: boolean;
  showInactiveVehiclesText?: boolean;
  showStatusTagOnItems?: boolean;
  sortable?: boolean;
  sortExcludedItems?: TCustomizableItemList[];
  wrapperClassName?: string;
};

const Items: FC<ItemsProps> = ({
  title,
  searchPlaceholder,
  searchCallback,
  listOfItems,
  handleCheckboxChange,
  disabled,
  groupPage,
  showInactiveVehicles,
  showInactiveVehiclesText = false,
  showStatusTagOnItems = false,
  sortable = false,
  sortExcludedItems,
  wrapperClassName = "col-span-4",
}) => {
  const { t } = useTranslation("translation", { keyPrefix: "dualListbox" });

  const droppableId = `droppableId-${title}`;

  return (
    <div className={wrapperClassName}>
      <label
        className={`flex justify-between mb-2 text-sm font-display font-semibold text-field-label-valid`}
      >
        {title}
        {showInactiveVehiclesText && (
          <span className="flex gap-2">
            <span className="text-sm text-accent-green">
              {" "}
              {t("active")}
              {":"}{" "}
              {(listOfItems?.filter((item) => item.isactive === 1)).length}
            </span>
            <span className="text-sm text-field-error-border">
              {" "}
              {t("inactive")}
              {":"}{" "}
              {(listOfItems?.filter((item) => item.isactive !== 1)).length}
            </span>
          </span>
        )}
      </label>
      <AppSearchBox
        placeholder={searchPlaceholder}
        onChange={searchCallback}
        onClear={() => {
          searchCallback({
            target: { value: "" },
          } as ChangeEvent<HTMLInputElement>);
        }}
      />
      <div className="h-96 mt-4 p-2 border border-gray-500 rounded-lg overflow-y-auto">
        {/* Items */}
        {sortable ? (
          <>
            {/* Fixed items */}
            {!!sortExcludedItems && sortExcludedItems?.length > 0 && (
              <div className="space-y-2 mb-2">
                {sortExcludedItems?.map((item: any) => {
                  return (
                    <DualListboxCheckboxItem
                      key={`${droppableId}__${item.id}`}
                      id={item.id}
                      title={item.title || item.id}
                      sub_title={item.sub_title}
                      handleChange={(e) =>
                        handleCheckboxChange({
                          item: item,
                          isChecked: e.target.checked,
                        })
                      }
                      show={
                        groupPage
                          ? showInactiveVehicles
                            ? true
                            : item.isactive === 1
                          : true
                      }
                      // disabled={disabled}
                      disabled={true}
                      {...(showStatusTagOnItems && {
                        statusTag: item.isactive === 1,
                      })}
                      isSortableItem={false}
                    />
                  );
                })}
              </div>
            )}

            {/* Sortable items */}
            <Droppable droppableId={droppableId}>
              {(provided) => (
                <div
                  className="space-y-2"
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                >
                  {listOfItems
                    ?.filter((item) => !sortExcludedItems?.includes(item))
                    ?.map((item: any) => {
                      return (
                        <DualListboxCheckboxItem
                          key={`${droppableId}__${item.id}`}
                          id={item.id}
                          title={item.title || item.id}
                          sub_title={item.sub_title}
                          handleChange={(e) =>
                            handleCheckboxChange({
                              item: item,
                              isChecked: e.target.checked,
                            })
                          }
                          show={
                            groupPage
                              ? showInactiveVehicles
                                ? true
                                : item.isactive === 1
                              : true
                          }
                          disabled={disabled}
                          {...(showStatusTagOnItems && {
                            statusTag: item.isactive === 1,
                          })}
                          isSortableItem={true}
                          draggableIndex={listOfItems.findIndex(
                            (listItem) => listItem.id === item.id
                          )}
                        />
                      );
                    })}
                </div>
              )}
            </Droppable>
          </>
        ) : (
          <div className="space-y-2">
            {listOfItems?.map((item: any) => {
              return (
                <DualListboxCheckboxItem
                  key={item.id}
                  id={item.id}
                  title={item.title || item.id}
                  sub_title={item.sub_title}
                  handleChange={(e) =>
                    handleCheckboxChange({
                      item: item,
                      isChecked: e.target.checked,
                    })
                  }
                  show={
                    groupPage
                      ? showInactiveVehicles
                        ? true
                        : item.isactive === 1
                      : true
                  }
                  disabled={disabled}
                  {...(showStatusTagOnItems && {
                    statusTag: item.isactive === 1,
                  })}
                />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

type DualListboxProps = {
  leftTitle: string;
  leftSearchPlaceholder: string;
  leftSearchCallback?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  leftList: TCustomizableItemList[];
  disabled?: boolean;
  disabledSubmit?: boolean;
  rightTitle: string;
  rightSearchPlaceholder: string;
  rightSearchCallback?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  rightList: TCustomizableItemList[];
  rightSelectCallback?: (item: TCustomizableItemList[]) => void;
  handleCancelButton: () => void;
  handleSaveButton: (currentAllList: TCustomizableItemList[]) => void;
  saveButtonEnabled?: boolean;
  groupPage?: boolean;
  showInactiveVehicles?: boolean;
  showInactiveVehiclesText?: boolean;
  saveButtonText?: string;
  showStatusTagOnItems?: boolean;
  displayRightSideOnly?: boolean; // used when viewing only ACL is applicable
  rightSortable?: boolean;
  rightSortExcludedItems?: TCustomizableItemList[];
};

export const DualListbox: FC<DualListboxProps> = ({
  leftTitle,
  leftSearchPlaceholder,
  leftSearchCallback,
  leftList,
  disabled,
  disabledSubmit,
  rightTitle,
  rightSearchCallback,
  rightSearchPlaceholder,
  rightList,
  rightSelectCallback,
  handleCancelButton,
  handleSaveButton,
  saveButtonEnabled = true,
  groupPage = false,
  showInactiveVehicles = false,
  showInactiveVehiclesText = false,
  saveButtonText = "Save",
  showStatusTagOnItems = false,
  displayRightSideOnly = false,
  rightSortable = false,
  rightSortExcludedItems,
}) => {
  const { t: tMain } = useTranslation();
  const { t } = useTranslation("translation", { keyPrefix: "dualListbox" });

  // current list mechainism
  const [currentAllList, setCurrentAllList] =
    React.useState<TCustomizableItemList[]>(leftList);
  const [filteredLeftList, setFilteredLeftList] =
    React.useState<TCustomizableItemList[]>(leftList);
  const [currentList, setCurrentList] =
    React.useState<TCustomizableItemList[]>(rightList);
  const [filteredRightList, setFilteredRightList] =
    React.useState<TCustomizableItemList[]>(rightList);

  // selected mechainism
  const [leftSelectedItems, setleftSelectedItems] = React.useState<
    TCustomizableItemList[]
  >([]);
  const [rightSelectedItems, setrightSelectedItems] = React.useState<
    TCustomizableItemList[]
  >([]);

  const [editStart, setEditStart] = React.useState(false); // To check if user has moved items from left to right or vice versa

  React.useEffect(() => {
    if (!editStart) {
      // Do not update list if user has moved items from left to right or vice versa
      setCurrentList(rightList);
    }
  }, [rightList, editStart]);

  React.useEffect(() => {
    if (!editStart) {
      // Do not update list if user has moved items from left to right or vice versa
      setCurrentAllList(
        leftList.filter(
          (item) =>
            !currentList.some((currentItem) => currentItem.id === item.id)
        )
      );
    }
  }, [leftList, currentList, editStart]);

  // handle left checkbox selection
  const handleLeftCheckboxChange = ({
    item,
    isChecked,
  }: {
    item: any;
    isChecked: boolean;
  }) => {
    if (isChecked) {
      setleftSelectedItems([...leftSelectedItems, item]);
    } else {
      setleftSelectedItems(
        leftSelectedItems.filter((currentItem) => currentItem.id !== item.id)
      );
    }
  };

  // handle right checkbox selection
  const handleRightCheckboxChange = ({
    item,
    isChecked,
  }: {
    item: any;
    isChecked: boolean;
  }) => {
    let newItems = [...rightSelectedItems];
    if (isChecked) {
      newItems = [...rightSelectedItems, item];
    } else {
      newItems = rightSelectedItems.filter(
        (currentItem) => currentItem.id !== item.id
      );
    }
    setrightSelectedItems(newItems);
  };

  // trigger right selection callback on filteredRightList change
  React.useEffect(() => {
    rightSelectCallback && rightSelectCallback(filteredRightList);
  }, [filteredRightList]);

  // handle add all vehicles from all vehicles
  const handleAddAll = () => {
    setEditStart(true); // User has moved items from left to right
    setCurrentList([...currentList, ...currentAllList]);
    setFilteredRightList([...filteredRightList, ...currentAllList]);
    setCurrentAllList([]);
    setFilteredLeftList([]);
    setleftSelectedItems([]);
  };

  // handle add selected items from all items
  const handleAddSelected = () => {
    setEditStart(true); // User has moved items from left to right
    setCurrentList([...currentList, ...leftSelectedItems]);
    setFilteredRightList([...filteredRightList, ...leftSelectedItems]);
    setCurrentAllList(
      currentAllList.filter(
        (item) =>
          !leftSelectedItems.some((currentItem) => currentItem.id === item.id)
      )
    );
    setFilteredLeftList(
      filteredLeftList.filter(
        (item) =>
          !leftSelectedItems.some((currentItem) => currentItem.id === item.id)
      )
    );
    setleftSelectedItems([]);
  };

  // handle remove selected items from group items
  const handleRemoveSelected = () => {
    setEditStart(true); // User has moved items from right to left
    setCurrentAllList([...currentAllList, ...rightSelectedItems]);
    setFilteredLeftList([...filteredLeftList, ...rightSelectedItems]);
    setCurrentList(
      currentList.filter(
        (item) =>
          !rightSelectedItems.some((currentItem) => currentItem.id === item.id)
      )
    );
    setFilteredRightList(
      filteredRightList.filter(
        (item) =>
          !rightSelectedItems.some((currentItem) => currentItem.id === item.id)
      )
    );
    setrightSelectedItems([]);
  };

  // handle remove all vehicles from group vehicles
  const handleRemoveAll = () => {
    setEditStart(true); // User has moved items from right to left
    setCurrentAllList([...currentAllList, ...currentList]);
    setFilteredLeftList([...filteredLeftList, ...currentList]);
    setCurrentList([]);
    setFilteredRightList([]);
    setrightSelectedItems([]);
  };

  // handle cancel button
  const handleCancel = () => {
    setCurrentList(rightList);
    setFilteredRightList(rightList);
    handleCancelButton();
  };

  const onLeftSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    setFilteredLeftList(
      currentAllList.filter((item) =>
        item.title.toLowerCase().includes(e.target.value.toLowerCase())
      )
    );
  };

  const onRightSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    setFilteredRightList(
      currentList.filter((item) =>
        item.title.toLowerCase().includes(e.target.value.toLowerCase())
      )
    );
  };

  const onDragEnd = (result: any) => {
    if (!result.destination) {
      return;
    }

    const items = Array.from(filteredRightList);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setFilteredRightList(items);
  };

  return (
    <>
      <DragDropContext
        onDragEnd={onDragEnd}
        dragHandleUsageInstructions="Use the handle to drag"
      >
        {/* <div className="grid grid-cols-12 gap-4"> */}

        {/* ---- LEFT SIDE ---- */}
        {!displayRightSideOnly && (
          <Items
            title={leftTitle}
            searchPlaceholder={leftSearchPlaceholder}
            searchCallback={
              leftSearchCallback ? leftSearchCallback : onLeftSearchChange
            }
            listOfItems={leftSearchCallback ? currentAllList : filteredLeftList}
            handleCheckboxChange={handleLeftCheckboxChange}
            disabled={disabled}
            groupPage={groupPage}
            showInactiveVehicles={showInactiveVehicles}
            showStatusTagOnItems={showStatusTagOnItems}
          />
        )}

        {/* ---- BUTTONS ---- */}
        {!displayRightSideOnly && (
          <div className="col-span-4 flex flex-col space-y-2">
            <div className="hidden md:block h-20"></div>
            <div className="relative">
              <AdminFormFieldSubmit
                type="button"
                variant="primary-like"
                label={
                  <>
                    <span className="hidden xl:block mr-2">{t("add_all")}</span>
                    <img
                      src={svgIcons.FastForwardIcon}
                      alt="fast-forward-icon"
                      className="size-5"
                    />
                  </>
                }
                titleText={t("add_all")}
                onClick={handleAddAll}
                disabled={disabled || currentAllList.length === 0}
              />
            </div>
            <div className="relative">
              <AdminFormFieldSubmit
                type="button"
                variant="secondary"
                label={
                  <>
                    <span className="hidden xl:block mr-2">
                      {t("add_selected")}
                    </span>
                    <img
                      src={svgIcons.ForwardIcon}
                      alt="fast-forward-icon"
                      className="size-5"
                    />
                  </>
                }
                titleText={t("add_selected")}
                onClick={handleAddSelected}
                disabled={disabled || leftSelectedItems.length === 0}
              />
            </div>
            <div className="relative">
              <AdminFormFieldSubmit
                type="button"
                variant="secondary"
                label={
                  <>
                    <img
                      src={svgIcons.BackwardIcon}
                      alt="fast-forward-icon"
                      className="size-5"
                    />
                    <span className="hidden xl:block ml-2">
                      {t("remove_selected")}
                    </span>
                  </>
                }
                titleText={t("remove_selected")}
                onClick={handleRemoveSelected}
                disabled={disabled || rightSelectedItems.length === 0}
              />
            </div>
            <div className="relative">
              <AdminFormFieldSubmit
                type="button"
                variant="primary-like"
                label={
                  <>
                    <img
                      src={svgIcons.FastBackwardIcon}
                      alt="fast-forward-icon"
                      className="size-5"
                    />
                    <span className="hidden xl:block ml-2">
                      {t("remove_all")}
                    </span>
                  </>
                }
                titleText={t("remove_all")}
                onClick={handleRemoveAll || currentList.length === 0}
                disabled={disabled || currentList.length === 0}
              />
            </div>
            <div className="flex-grow"></div>
            <AdminFormFieldSubmit
              type="button"
              variant="primary"
              label={tMain("cancel")}
              onClick={handleCancel}
              disabled={disabled}
            />
            <AdminFormFieldSubmit
              type="button"
              variant="success"
              label={saveButtonText}
              onClick={() => handleSaveButton(currentList)}
              disabled={disabled || disabledSubmit || !saveButtonEnabled}
            />
          </div>
        )}

        {/* ---- RIGHT SIDE ---- */}

        <Items
          title={rightTitle}
          searchPlaceholder={rightSearchPlaceholder}
          searchCallback={
            rightSearchCallback ? rightSearchCallback : onRightSearchChange
          }
          listOfItems={rightSearchCallback ? currentList : filteredRightList}
          handleCheckboxChange={handleRightCheckboxChange}
          disabled={disabled}
          groupPage={groupPage}
          showInactiveVehicles={showInactiveVehicles}
          showInactiveVehiclesText={showInactiveVehiclesText}
          showStatusTagOnItems={showStatusTagOnItems}
          sortable={rightSortable}
          sortExcludedItems={rightSortExcludedItems}
          wrapperClassName={displayRightSideOnly ? "col-span-12" : "col-span-4"}
        />
        {/* </div > */}
      </DragDropContext>
    </>
  );
};

export default DualListbox;
